import {useLazyQuery} from "@apollo/client";
import debounce from "lodash/debounce";
import {useCallback, useEffect, useState} from "react";
import {components, MultiValue, OptionProps} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {CheckInfo, FindChecksResult, GQL_FIND_CHECKS} from "@/lib/queries/statuspages.ts";

interface CheckMultiSelectProps {
  /** Ties the picker to its FormLabel, which insists on a real control id. */
  inputId: string;
  value: number[];
  onChange: (checkIds: number[]) => void;
  /** Called with the full records whenever ids are resolved, however they arrived. */
  onChecksResolved: (checks: CheckInfo[]) => void;
}

const CheckOption = ({data, ...props}: OptionProps<CheckInfo, true>) => (
  <components.Option {...props} data={data}>
    <div className="pulse-color-gray-600 float-end">{data.type}</div>
    <div className="text-truncate">{data.name}</div>
    <div className="pulse-color-gray-600 pulseTextBodyTiny">{data.address}</div>
  </components.Option>
);

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 50;

/**
 * Multi-check picker for one builder row.
 *
 * `ServiceSelect` covers the same search but is typed single-select and has a dozen
 * callers, so it is composed from `Select` here rather than made generic in place.
 */
export const CheckMultiSelect = ({inputId, value, onChange, onChecksResolved}: CheckMultiSelectProps) => {
  const [searchChecks] = useLazyQuery<FindChecksResult>(GQL_FIND_CHECKS);
  const [selected, setSelected] = useState<CheckInfo[]>([]);

  // A config restored from the URL or a preset link carries ids but no names. Resolve them
  // so the chips read as check names, and hand the records up: the rail needs each check's
  // type to narrow the metric catalog, and without it no data type or metric can be picked.
  useEffect(() => {
    const missing = value.filter((id) => !selected.some((check) => check.id === id));
    if (missing.length === 0) {
      return;
    }
    searchChecks({variables: {search: "", limit: missing.length, include: missing}}).then(({data}) => {
      const found = data?.findChecks?.items ?? [];
      if (found.length === 0) {
        return;
      }
      setSelected((previous) => [
        ...previous,
        ...found.filter((check) => !previous.some((existing) => existing.id === check.id)),
      ]);
      onChecksResolved(found);
    });
  }, [value, selected, searchChecks, onChecksResolved]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptions = useCallback(
    debounce((inputValue: string, setOptions: (options: CheckInfo[]) => void) => {
      searchChecks({variables: {search: inputValue, limit: SEARCH_LIMIT}}).then(({data}) =>
        setOptions(data?.findChecks?.items ?? [])
      );
    }, SEARCH_DEBOUNCE_MS),
    [searchChecks]
  );

  const handleChange = (next: MultiValue<CheckInfo>) => {
    const checks = [...next];
    setSelected(checks);
    onChecksResolved(checks);
    onChange(checks.map((check) => check.id));
  };

  return (
    <Select<CheckInfo, true>
      inputId={inputId}
      isMulti
      async
      defaultOptions
      cacheOptions
      loadOptions={loadOptions}
      value={selected.filter((check) => value.includes(check.id))}
      onChange={handleChange}
      getOptionValue={(check) => String(check.id)}
      getOptionLabel={(check) => check.name}
      components={{Option: CheckOption}}
      placeholder="Select checks…"
      size="small"
    />
  );
};
